import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import dayjs from 'dayjs';
import { CalendarDays, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { usePlannerEvents } from '../../features/calendar/hooks/usePlannerEvents.js';
import { formatWeekRangeTitle, weekDays } from '../../features/calendar/lib/plannerEvents.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

export interface WeekStripWidgetProps {
  className?: string;
  testId?: string;
  onOpenCalendar?: () => void;
}

/**
 * «Неделя» — full-width weekly ribbon (spec D `.week-grid`): seven compact
 * day columns with 22px event chips (time + title), today marked with the
 * amber edge, the week total in the header and a link to the planner.
 * Data comes from the existing planner sources (tasks/meetings/deal
 * deadlines) via usePlannerEvents — nothing is fabricated.
 */
export function WeekStripWidget({
  className,
  testId,
  onOpenCalendar,
}: WeekStripWidgetProps): ReactElement {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const anchor = dayjs();
  const { events, isLoading } = usePlannerEvents(anchor);
  const canOpenCalendar = typeof onOpenCalendar === 'function';

  const days = weekDays(anchor.startOf('day'));
  const todayKey = anchor.format('YYYY-MM-DD');
  const eventsByDay = new Map<string, typeof events>();
  for (const event of events) {
    const list = eventsByDay.get(event.dateKey) ?? [];
    list.push(event);
    eventsByDay.set(event.dateKey, list);
  }
  for (const list of eventsByDay.values()) {
    list.sort((a, b) => (a.timeMinutes ?? -1) - (b.timeMinutes ?? -1));
  }

  const widgetContainerProps = {
    title: formatWeekRangeTitle(anchor, locale),
    icon: CalendarDays,
    iconColor: 'var(--ant-color-primary-border-hover, #d97706)',
    minHeight: 160,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <span className="flex items-center gap-2">
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 550,
            whiteSpace: 'nowrap',
            color: 'var(--ant-color-primary-text, #b45309)',
            border:
              '1px solid color-mix(in srgb, var(--ant-color-primary-border-hover, #d97706) 45%, transparent)',
            background: 'color-mix(in srgb, var(--ant-color-primary-bg, #fffbeb) 80%, transparent)',
            borderRadius: 999,
            padding: '0 8px',
            lineHeight: '17px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {t('crm.dashboard.week_strip.events_count', { count: events.length })}
        </span>
        <button
          type="button"
          {...(canOpenCalendar ? { onClick: onOpenCalendar } : {})}
          disabled={!canOpenCalendar}
          className="flex items-center gap-1 font-medium"
          style={{
            fontSize: 11,
            color: canOpenCalendar
              ? 'var(--ant-color-primary-text, #b45309)'
              : 'var(--ant-color-text-tertiary, #6d6963)',
            cursor: canOpenCalendar ? 'pointer' : 'default',
            transition: 'opacity 110ms ease',
          }}
        >
          {t('crm.dashboard.week_strip.calendar_link')} <ArrowUpRight size={10} />
        </button>
      </span>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {isLoading ? (
        <div
          className="animate-pulse"
          style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {[1, 2].map((index) => (
            <div
              key={index}
              style={{
                height: 30,
                borderRadius: 'var(--ant-border-radius-sm, 6px)',
                background: 'var(--g-fill, #f5f1eb)',
              }}
            />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <CalendarDays size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.week_strip.empty')}
          </span>
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7"
          style={{ gap: 0, minWidth: 0 }}
        >
          {days.map((day) => {
            const dayKey = day.format('YYYY-MM-DD');
            const dayEvents = eventsByDay.get(dayKey) ?? [];
            const isToday = dayKey === todayKey;

            return (
              <div
                key={dayKey}
                style={{
                  minWidth: 0,
                  position: 'relative',
                  borderTop: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
                }}
              >
                {isToday ? (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: -1,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: 'var(--ant-color-primary-border-hover, #d97706)',
                    }}
                  />
                ) : null}
                <div className="flex items-baseline gap-1.5" style={{ padding: '8px 10px 6px' }}>
                  <span
                    className="uppercase"
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      color: 'var(--ant-color-text-tertiary, #6d6963)',
                    }}
                  >
                    {day.locale(locale).format('dd')}
                  </span>
                  <span
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                      fontSize: 11,
                      color: isToday
                        ? 'var(--ant-color-primary-text, #b45309)'
                        : 'var(--ant-color-text-secondary, #57534e)',
                      fontWeight: isToday ? 600 : 400,
                    }}
                  >
                    {day.format('DD')}
                  </span>
                  <span
                    className="ml-auto"
                    style={{
                      fontSize: 10,
                      color: 'var(--ant-color-text-tertiary, #6d6963)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {dayEvents.length > 0 ? dayEvents.length : ''}
                  </span>
                </div>
                <div
                  style={{
                    padding: '6px 6px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  {dayEvents.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => navigate(event.href)}
                      className="crm-hover-fill flex w-full items-center gap-1.5 border-0 bg-transparent text-left"
                      style={{
                        height: 22,
                        padding: '0 5px',
                        borderRadius: 'var(--ant-border-radius-sm, 6px)',
                        ...(event.overdue
                          ? { background: 'var(--ant-color-error-bg, #fef2f2)' }
                          : {}),
                      }}
                      title={event.title}
                    >
                      {event.timeLabel ? (
                        <span
                          style={{
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                            fontSize: 10,
                            color: event.overdue
                              ? 'var(--ant-color-text-secondary, #57534e)'
                              : 'var(--ant-color-text-tertiary, #6d6963)',
                            flex: 'none',
                            width: 34,
                          }}
                        >
                          {event.timeLabel}
                        </span>
                      ) : null}
                      <span
                        className="truncate"
                        style={{
                          fontSize: 11,
                          fontWeight: 550,
                          color: event.overdue
                            ? 'var(--ant-color-error, #b91c1c)'
                            : 'var(--ant-color-text, #292524)',
                          textDecoration: event.done ? 'line-through' : 'none',
                        }}
                      >
                        {event.title}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WidgetContainer>
  );
}
